import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Tooltip } from '@qodo/design-system';
import { ChevronRight } from 'lucide-react';
import { GuardedLink, useNavigationGuard } from 'shared/NavigationGuard';
import { TAB_INDEX } from '../../consts/html';
import { ROUTES } from '../../routes/consts';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../ui/collapsible';
import {
  Sidebar as ShadcnSidebar,
  SidebarContent,
  SidebarFooter,
  SidebarMenuItem,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarGroup,
  useSidebar,
} from '../../ui/sidebar';
import { cn } from '../../utils/className';
import { isRouteActive } from '../../utils/route';
import { spawn } from '../../utils/translations';
import { AppVersion } from '../AppVersion';
import { LogoHeader } from './parts/LogoHeader';
import { SidebarNavSkeleton } from './parts/SidebarNavSkeleton';
import { UpgradeNow } from './parts/UpgradeNow';
import { UserMenu } from './parts/UserMenu';
import { Section, GroupItem, SubItem, SidebarProps } from './types';
import { useSidebarConfiguration } from './useSidebarConfiguration';

const Sidebar = ({ className: propsClassName }: SidebarProps) => {
  const { sections, isLoading } = useSidebarConfiguration();

  // Show the full skeleton only on the initial load. Once the sidebar-blocking
  // flags have resolved once, keep the nav items visible even if a background
  // refetch briefly marks a flag as loading again (e.g. on page navigation).
  const [hasLoadedOnce, setHasLoadedOnce] = useState(!isLoading);

  useEffect(() => {
    if (!isLoading) setHasLoadedOnce(true);
  }, [isLoading]);

  const showSkeleton = !hasLoadedOnce && isLoading;
  const skeletonCount = showSkeleton
    ? sections.reduce((acc, s) => acc + s.items.length, 0)
    : 0;
  const navigate = useNavigate();
  const { guardedNavigate } = useNavigationGuard();
  const { open, state } = useSidebar();
  const isCollapsed = state === 'collapsed';
  const spawnDisabledReasonTooltip = spawn('sidebar.tooltips');

  const renderSubItem = (subItem: SubItem) => {
    const { id, title, href = '#', badgeLabel } = subItem;
    const isActive = isRouteActive(href);
    const disabledReason = subItem.disabledReason || false;
    const isDisabled = !!disabledReason;

    if (subItem.shouldHide) {
      return <React.Fragment key={subItem.id}></React.Fragment>;
    }

    const renderTitle = () => {
      const titleElement = <div className="whitespace-nowrap"> {title}</div>;

      if (isDisabled) {
        return (
          <Tooltip
            key={id}
            content={spawnDisabledReasonTooltip(disabledReason)}
            contentProps={{ side: 'right', disableArrow: false }}
          >
            <span className="pointer-events-auto">{titleElement}</span>
          </Tooltip>
        );
      }

      return titleElement;
    };

    return (
      <SidebarMenuSubItem key={id}>
        <SidebarMenuSubButton
          className="cursor-pointer"
          isActive={isActive}
          aria-disabled={isDisabled}
          tabIndex={isDisabled ? TAB_INDEX.DISABLED : TAB_INDEX.ENABLED}
          asChild
        >
          <GuardedLink to={href} disabled={isDisabled}>
            <div className="flex gap-2 items-center">
              {renderTitle()}
              {badgeLabel && <Badge>{badgeLabel}</Badge>}
            </div>
          </GuardedLink>
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    );
  };

  const renderCollapsedSubMenuItem = (subItem: SubItem) => {
    const { id, title, href = '#', badgeLabel } = subItem;

    if (subItem.shouldHide) {
      return <React.Fragment key={id}></React.Fragment>;
    }

    const isActive = isRouteActive(href);
    const disabledReason = subItem.disabledReason || false;
    const isDisabled = !!disabledReason;

    const titleElement = <span className="whitespace-nowrap">{title}</span>;

    return (
      <GuardedLink
        key={id}
        to={href}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        tabIndex={isDisabled ? TAB_INDEX.DISABLED : TAB_INDEX.ENABLED}
        className={cn(
          'flex items-center gap-2 whitespace-nowrap rounded-sm px-2 py-1.5 text-body text-text-general-neutral-strong hover:bg-fill-interactive-neutral-idle',
          {
            'bg-fill-interactive-neutral-idle': isActive,
            'opacity-50': isDisabled,
          },
        )}
      >
        {isDisabled ? (
          <Tooltip
            content={spawnDisabledReasonTooltip(disabledReason)}
            contentProps={{ side: 'right', disableArrow: false }}
          >
            <span className="pointer-events-auto">{titleElement}</span>
          </Tooltip>
        ) : (
          titleElement
        )}
        {badgeLabel && <Badge>{badgeLabel}</Badge>}
      </GuardedLink>
    );
  };

  const renderCollapsedSubMenu = (groupItem: GroupItem) => (
    <>
      <div className="px-2 py-1 text-xs font-medium text-text-general-neutral-subtle">
        {groupItem.title}
      </div>
      {groupItem.subItems?.map(renderCollapsedSubMenuItem)}
    </>
  );

  const renderGroupItem = (groupItem: GroupItem) => {
    const hasSubItems = groupItem.subItems && groupItem.subItems.length > 0;
    const disabledReason = groupItem.disabledReason || false;

    if (groupItem.shouldHide) {
      return <React.Fragment key={groupItem.id}></React.Fragment>;
    }

    // If the item has no subItems, render it as a regular item
    if (!hasSubItems) {
      const isActive = isRouteActive(groupItem.href);
      const isDisabled = !!disabledReason;

      const renderGroupTitle = () => {
        const titleElement = (
          <div className="whitespace-nowrap">{groupItem.title}</div>
        );

        if (isDisabled) {
          return (
            <Tooltip
              content={spawnDisabledReasonTooltip(disabledReason)}
              contentProps={{ side: 'right', disableArrow: false }}
            >
              <span className="pointer-events-auto">{titleElement}</span>
            </Tooltip>
          );
        }

        return titleElement;
      };

      return (
        <SidebarMenuItem key={groupItem.id} disabled={isDisabled}>
          <SidebarMenuButton
            tooltip={groupItem.title}
            isActive={isActive}
            asChild
          >
            <GuardedLink
              to={groupItem.href || '#'}
              aria-disabled={isDisabled}
              tabIndex={isDisabled ? TAB_INDEX.DISABLED : TAB_INDEX.ENABLED}
              onClick={groupItem.onClick}
              className={cn({
                'pointer-events-none opacity-50': isDisabled,
              })}
            >
              {groupItem.icon}
              <div className="flex gap-2 items-center">
                {renderGroupTitle()}
                {groupItem.badgeLabel && <Badge>{groupItem.badgeLabel}</Badge>}
              </div>
            </GuardedLink>
          </SidebarMenuButton>
        </SidebarMenuItem>
      );
    }

    const shouldWrapIconWithLinkComponent = !open && groupItem.iconLink;

    // Check if any subitem is active (excluding hidden subitems)
    const isSubItemActive = groupItem.subItems?.some(
      (subItem) =>
        !subItem.shouldHide && subItem.href && isRouteActive(subItem.href),
    );

    const renderSideBarMenuButton = () => {
      const checkPath = groupItem.iconLink || groupItem.href;

      // Mark parent as active if any subitem is active, or on collapsed desktop when path matches
      const isActive =
        (isCollapsed && isSubItemActive) || (!open && isRouteActive(checkPath));

      return (
        <SidebarMenuButton
          tooltip={isCollapsed ? undefined : groupItem.title}
          disabled={false}
          onClick={groupItem.onClick}
          isActive={isActive}
        >
          {groupItem.icon}
          <div className="whitespace-nowrap flex justify-between w-full">
            {groupItem.title}
            {groupItem.badgeLabel && <Badge>{groupItem.badgeLabel}</Badge>}
          </div>
          <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 text-text-general-neutral-mid" />
        </SidebarMenuButton>
      );
    };

    const renderSideBarMenuTrigger = () => {
      if (shouldWrapIconWithLinkComponent) {
        return (
          <GuardedLink to={groupItem.iconLink!} className="w-full">
            {renderSideBarMenuButton()}
          </GuardedLink>
        );
      }

      return renderSideBarMenuButton();
    };

    // When collapsed, the inline sub-items are hidden, so reveal them in a
    // hover flyout anchored to the right of the collapsed parent icon.
    if (isCollapsed) {
      return (
        <SidebarMenuItem key={groupItem.id}>
          <Tooltip
            content={renderCollapsedSubMenu(groupItem)}
            triggerProps={{ asChild: true }}
            contentProps={{
              side: 'right',
              align: 'start',
              disableArrow: true,
              className:
                'flex flex-col gap-0.5 min-w-44 p-1 bg-app-background border-border-bg-neutral text-text-general-neutral-strong shadow-md',
            }}
          >
            {renderSideBarMenuTrigger()}
          </Tooltip>
        </SidebarMenuItem>
      );
    }

    // Otherwise, render it as a collapsible item with subItems
    return (
      <Collapsible
        key={groupItem.id}
        asChild
        defaultOpen={groupItem.defaultOpen || isSubItemActive}
        className="group/collapsible"
      >
        <SidebarMenuItem>
          <CollapsibleTrigger asChild>
            {renderSideBarMenuTrigger()}
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenuSub>
              {groupItem.subItems?.map(renderSubItem)}
            </SidebarMenuSub>
          </CollapsibleContent>
        </SidebarMenuItem>
      </Collapsible>
    );
  };

  const renderSection = (section: Section) => (
    <SidebarGroup key={section.id} className="text-text-general-neutral-strong">
      <SidebarMenu>{section.items.map(renderGroupItem)}</SidebarMenu>
    </SidebarGroup>
  );

  const onLogoClick = () => {
    guardedNavigate(() => navigate(ROUTES.HOME));
  };

  const sidebarContent = showSkeleton ? (
    <SidebarNavSkeleton count={skeletonCount} />
  ) : (
    sections.map(renderSection)
  );

  return (
    <ShadcnSidebar
      className={cn('group/header', propsClassName)}
      collapsible="icon"
    >
      <LogoHeader onClick={onLogoClick} />
      <SidebarContent className="p-0 gap-2">{sidebarContent}</SidebarContent>
      <SidebarFooter className="p-2">
        <AppVersion />
        <UpgradeNow />
        <UserMenu />
      </SidebarFooter>
    </ShadcnSidebar>
  );
};

export default Sidebar;
